import {
  DomainFault,
  type DomainModule,
  type DomainReadContext,
  type JsonObject,
  type UsageEntry,
} from '../../contracts';
import type { Entity, Kind, Role } from '../../types';
import { commitment, visibleFollowUps } from '../student';
import type {
  MetricDefinition,
  MetricScope,
  PilotGoal,
  PilotState,
  PricingVersion,
  ReportSnapshot,
  ScopedMetricSnapshot,
  UsageSummary,
} from './types';

export * from './types';

function fail(message: string, code = 'VALIDATION'): never {
  throw new DomainFault(code, message);
}
export const definitions: MetricDefinition[] = [
  {
    id: 'recordings',
    label: '业务录音',
    unit: '条',
    formula: '按录音开始时间统计 UUID 去重数',
    missingRule: '无有效开始时间单列缺失',
  },
  {
    id: 'clues',
    label: '人工确认线索',
    unit: '条',
    formula: '有效 confirmed 线索 / 周期创建线索；合并重复排除',
    missingRule: 'AI候选不计确认',
  },
  {
    id: 'cases',
    label: '事项关闭',
    unit: '项',
    formula: '周期创建且当前 closed 事项 / 周期创建事项',
    missingRule: '重开不计关闭，缺期限单列',
  },
  {
    id: 'knowledge',
    label: '有效发布案例',
    unit: '篇',
    formula: '当前 published 案例 / 周期创建案例',
    missingRule: '草稿与撤回不计发布',
  },
  {
    id: 'plans',
    label: '改进计划完成',
    unit: '项',
    formula: '周期创建 completed 计划 / 周期创建计划',
    missingRule: '候选观察不计正式计划',
  },
  {
    id: 'service',
    label: '服务跟进完成',
    unit: '项',
    formula: '周期创建 completed 跟进 / 周期创建正式跟进',
    missingRule: '候选建议不计正式承诺',
  },
  {
    id: 'devices',
    label: '设备投入',
    unit: '台',
    formula: '当前范围非报废设备，按UUID去重；设备存量不按创建周期截断',
    missingRule: '无组织或报废设备排除',
  },
  {
    id: 'activeDevices',
    label: '设备有效采集',
    unit: '台',
    formula: '周期内有有效确认回执的设备 / 当前领用或启用设备',
    missingRule: '不作为在岗率；缺少启停历史，分母采用当前有效状态',
  },
  {
    id: 'files',
    label: '后台已确认文件',
    unit: '份',
    formula: '按采集confirmedAt统计有效非重复源文件',
    missingRule: '统一采集03账本；无回执不推定文件数',
  },
].map((d) => ({ ...d, version: 'O-C1:1' }));
const kinds: Record<string, Kind> = {
  recordings: 'recording',
  clues: 'clue',
  cases: 'case',
  knowledge: 'knowledge',
  plans: 'plan',
  service: 'followUp',
  files: 'recording',
  devices: 'device',
  activeDevices: 'device',
};
export function validateScope(scope: MetricScope) {
  if (
    !scope.campus ||
    !Number.isFinite(Date.parse(scope.from)) ||
    !Number.isFinite(Date.parse(scope.to)) ||
    Date.parse(scope.from) >= Date.parse(scope.to)
  )
    fail('请选择有效范围和左闭右开时间区间');
  return scope;
}
export function inScope(row: Entity, scope: MetricScope, time = row.createdAt) {
  return (
    (scope.campus === '全部校区' || row.campus === scope.campus) &&
    Date.parse(time) >= Date.parse(scope.from) &&
    Date.parse(time) < Date.parse(scope.to)
  );
}
export function metricSelector(
  ctx: DomainReadContext,
  scope: MetricScope,
): ScopedMetricSnapshot[] {
  validateScope(scope);
  if (ctx.context.campus !== '全部校区' && scope.campus !== ctx.context.campus)
    fail('统计范围超出当前校区', 'FORBIDDEN');
  return definitions.map((definition) => {
    if (definition.id === 'files') {
      const receipts = (ctx.data().collectionReceipts || []) as unknown as {
        uuid: string;
        recordingUuid: string;
        confirmedAt: string;
        campus: string;
      }[];
      const rows = receipts.filter(
        (r, i) =>
          receipts.findIndex((v) => v.uuid === r.uuid) === i &&
          ctx.get(r.recordingUuid) &&
          (scope.campus === '全部校区' || r.campus === scope.campus) &&
          Date.parse(r.confirmedAt) >= Date.parse(scope.from) &&
          Date.parse(r.confirmedAt) < Date.parse(scope.to),
      );
      return {
        definition,
        scope: { ...scope },
        asOf: ctx.now,
        revision: ctx.revision,
        numerator: rows.length,
        denominator: null,
        missing: 0,
        sourceUuids: rows.map((r) => r.recordingUuid),
        numeratorUuids: rows.map((r) => r.recordingUuid),
      };
    }
    if (definition.id === 'activeDevices') {
      const rows = ctx
        .list('device')
        .filter(
          (r) =>
            (scope.campus === '全部校区' || r.campus === scope.campus) &&
            ['assigned', 'active'].includes(r.status),
        );
      const receipts = (ctx.data().collectionReceipts || []) as unknown as {
        deviceUuid: string;
        confirmedAt: string;
      }[];
      const active = rows.filter((d) =>
        receipts.some(
          (r) =>
            r.deviceUuid === d.uuid &&
            Date.parse(r.confirmedAt) >= Date.parse(scope.from) &&
            Date.parse(r.confirmedAt) < Date.parse(scope.to),
        ),
      );
      return {
        definition,
        scope: { ...scope },
        asOf: ctx.now,
        revision: ctx.revision,
        numerator: active.length,
        denominator: rows.length,
        missing: 0,
        sourceUuids: rows.map((r) => r.uuid),
        numeratorUuids: active.map((r) => r.uuid),
      };
    }
    if (definition.id === 'devices') {
      const rows = ctx
        .list('device')
        .filter(
          (r) =>
            (scope.campus === '全部校区' || r.campus === scope.campus) &&
            r.campus &&
            r.status !== 'retired',
        );
      return {
        definition,
        scope: { ...scope },
        asOf: ctx.now,
        revision: ctx.revision,
        numerator: rows.length,
        denominator: null,
        missing: 0,
        sourceUuids: rows.map((r) => r.uuid),
        numeratorUuids: rows.map((r) => r.uuid),
      };
    }
    const sources =
      definition.id === 'service'
        ? visibleFollowUps(ctx).filter((r) => !!commitment(r)?.confirmedAt)
        : ctx.list(kinds[definition.id]);
    const eligible = sources.filter(
      (r) =>
        (scope.campus === '全部校区' || r.campus === scope.campus) &&
        r.status !== 'deleted' &&
        r.fields?.duplicate !== 'true' &&
        r.status !== 'merged' &&
        !(
          definition.id === 'plans' &&
          r.fields?.observationStatus === 'candidate'
        ) &&
        !(definition.id === 'service' && r.status === 'candidate'),
    );
    const rows = eligible.filter((r) =>
      inScope(
        r,
        scope,
        definition.id === 'recordings' ? r.startAt || '' : r.createdAt,
      ),
    );
    const successful = rows.filter(
      (r) =>
        definition.id === 'recordings' ||
        (definition.id === 'clues' && r.status === 'closed') ||
        r.status ===
          (
            {
              clues: 'confirmed',
              cases: 'closed',
              knowledge: 'published',
              plans: 'completed',
              service: 'completed',
            } as Record<string, string>
          )[definition.id],
    );
    return {
      definition,
      scope: { ...scope },
      asOf: ctx.now,
      revision: ctx.revision,
      numerator: successful.length,
      denominator: definition.id === 'recordings' ? null : rows.length,
      missing: eligible.filter(
        (r) =>
          !Number.isFinite(
            Date.parse(
              definition.id === 'recordings' ? r.startAt || '' : r.createdAt,
            ),
          ),
      ).length,
      sourceUuids: rows.map((r) => r.uuid),
      numeratorUuids: successful.map((r) => r.uuid),
    };
  });
}
export function backlog(ctx: DomainReadContext, scope: MetricScope) {
  validateScope(scope);
  return ['batch', 'recording', 'clue', 'case', 'plan', 'followUp', 'knowledge']
    .flatMap((kind) =>
      kind === 'followUp'
        ? visibleFollowUps(ctx).filter((r) => !!commitment(r)?.confirmedAt)
        : ctx.list(kind as Kind),
    )
    .filter(
      (r) =>
        inScope(r, scope) &&
        (r.kind === 'recording'
          ? r.fields?.linkStatus === 'pending'
          : r.kind === 'knowledge'
            ? r.status === 'pendingReview'
            : r.kind === 'batch'
              ? ['failed', 'paused', 'pending', 'transferring'].includes(
                  r.status,
                )
              : ![
                  'completed',
                  'closed',
                  'excluded',
                  'published',
                  'cancelled',
                  'merged',
                ].includes(r.status)),
    )
    .filter(
      (r) =>
        r.status !== 'candidate' && r.fields?.observationStatus !== 'candidate',
    )
    .filter((r, i, rows) => rows.findIndex((x) => x.uuid === r.uuid) === i);
}
export function summarizeUsage(
  entries: UsageEntry[],
  scope: MetricScope,
  ctx: DomainReadContext,
): UsageSummary {
  validateScope(scope);
  const unique = new Map<string, UsageEntry>();
  for (const entry of entries) {
    const row = ctx.get(entry.sourceUuid);
    if (!row || !inScope(row, scope, entry.occurredAt)) continue;
    if (
      !Number.isSafeInteger(entry.amountInCents) ||
      entry.amountInCents < 0 ||
      !Number.isFinite(entry.quantity) ||
      entry.quantity < 0
    )
      continue;
    const key =
      entry.status === 'succeeded'
        ? entry.businessKey
        : `failure:${entry.businessKey}`;
    if (!unique.has(key)) unique.set(key, entry);
  }
  const rows = [...unique.values()];
  return {
    entries: rows,
    totalCents: rows
      .filter((r) => r.status === 'succeeded')
      .reduce((n, r) => n + r.amountInCents, 0),
    successMs: rows
      .filter((r) => r.status === 'succeeded' && r.unit === 'millisecond')
      .reduce((n, r) => n + r.quantity, 0),
    failures: rows.filter((r) => r.status === 'failed').length,
  };
}
export function priceEstimate(
  minutes: number,
  gib: number,
  p: Pick<PricingVersion, 'centsPerMinute' | 'storageCentsPerGiB'>,
) {
  if (
    [minutes, gib, p.centsPerMinute, p.storageCentsPerGiB].some(
      (n) => !Number.isFinite(n) || n < 0,
    ) ||
    !Number.isSafeInteger(p.centsPerMinute) ||
    !Number.isSafeInteger(p.storageCentsPerGiB)
  )
    fail('数量和整数分单价无效');
  const cents =
    Math.ceil(minutes * p.centsPerMinute) +
    Math.ceil(gib * p.storageCentsPerGiB);
  if (!Number.isSafeInteger(cents)) fail('估算金额超出范围');
  return cents;
}
export const pilotOf = (row: Entity): PilotState =>
  JSON.parse(row.fields?.pilotState || 'null') || {
    goals: [],
    evidence: [],
    reports: [],
    decisions: [],
    deviceUuids: [],
    taskUuids: [],
  };
export const pilotScope = (row: Entity): MetricScope => ({
  campus: row.campus,
  from: row.startAt || '',
  to: row.endAt || '',
});
export function reportFor(ctx: DomainReadContext, row: Entity, uuid: string) {
  const report = pilotOf(row).reports.find((r) => r.uuid === uuid);
  if (!report) return undefined;
  return Object.keys(report.sourceVersions).every((id) => ctx.get(id))
    ? report
    : undefined;
}
export function reportDraft(
  ctx: DomainReadContext,
  row: Entity,
  note: string,
): Omit<ReportSnapshot, 'uuid' | 'version'> {
  const pilot = pilotOf(row),
    version = pilot.goals.at(-1);
  const goals = version?.items || [];
  const scopeValid =
    Number.isFinite(Date.parse(row.startAt || '')) &&
    Number.isFinite(Date.parse(row.endAt || '')) &&
    Date.parse(row.startAt || '') < Date.parse(row.endAt || '');
  const metrics = scopeValid ? metricSelector(ctx, pilotScope(row)) : [];
  const evidence = pilot.evidence.filter((e) => {
    const source = ctx.get(e.sourceUuid),
      goal = goals.find((g) => g.uuid === e.goalUuid);
    return (
      source?.revision === e.sourceRevision &&
      goal &&
      metrics
        .find((m) => m.definition.id === goal.metric)
        ?.numeratorUuids.includes(source.uuid)
    );
  });
  const missing: string[] = [];
  if (!scopeValid) missing.push('试点周期缺失或无效，请先配置评审安排');
  if (!goals.length) missing.push('尚未配置验收目标');
  for (const goal of goals) {
    const metric = metrics.find((m) => m.definition.id === goal.metric);
    if (!metric || metric.numerator < goal.target)
      missing.push(`${goal.scenario}：目标未达`);
    if (!evidence.some((e) => e.goalUuid === goal.uuid))
      missing.push(`${goal.scenario}：缺少有效人工验收材料`);
  }
  const allIds = new Set([
    ...metrics.flatMap((m) => m.sourceUuids),
    ...evidence.map((e) => e.sourceUuid),
    ...pilot.deviceUuids,
    ...pilot.taskUuids,
  ]);
  const sourceVersions: Record<string, number> = {};
  for (const id of allIds) {
    const r = ctx.get(id);
    if (!r) missing.push(`关联来源不可访问：${id}`);
    else sourceVersions[id] = r.revision;
  }
  return {
    generatedAt: ctx.now,
    goalVersion: version?.version || 0,
    scope: pilotScope(row),
    goals: structuredClone(goals),
    metrics,
    evidence: structuredClone(evidence),
    missing,
    note: note.trim() || '模拟评审材料，不代表真实业务收益',
    sourceVersions,
  };
}
function json<T>(text: string | undefined): T {
  try {
    return JSON.parse(text || '');
  } catch {
    return fail('输入数据格式无效');
  }
}
function target(ctx: DomainReadContext, id: string | undefined) {
  const r = ctx.get(id);
  return r?.kind === 'pilot' ? r : fail('试点不可访问', 'FORBIDDEN');
}
const write = (
  ctx: { touch: (r: Entity) => void },
  row: Entity,
  p: PilotState,
) => {
  row.fields = { ...row.fields, pilotState: JSON.stringify(p) };
  ctx.touch(row);
  return row;
};
const roles: Role[] = ['campusManager', 'groupOperator', 'regionManager'];
const recent = (fields: Record<string, string> | undefined) => {
  const at = Number(fields?.previewAt);
  if (!Number.isFinite(at) || Math.abs(Date.now() - at) > 900000)
    fail('预览已过期，请重新预览', 'CONFLICT');
};
export const operationsDomain: DomainModule = {
  namespace: 'operations',
  resource: 'operation',
  version: 1,
  commands: {
    assign: {
      roles,
      kind: 'task',
      execute(ctx, input) {
        const source = ctx.get(input.uuid);
        if (!source || !input.fields?.owner?.trim() || !input.note?.trim())
          fail('请填写负责人和具体行动');
        const open = ctx
          .list('task')
          .find(
            (t) =>
              t.sourceUuid === source.uuid &&
              t.status !== 'completed' &&
              t.fields?.operationAction === 'true',
          );
        if (open) return open;
        const row = ctx.append('task', input.note, {
          sourceUuid: source.uuid,
          campus: source.campus,
          memberUuid: source.memberUuid,
          owner: input.fields?.owner,
          dueAt: input.dueAt,
          status: 'pending',
          fields: { operationAction: 'true' },
        });
        return row;
      },
    },
    price: {
      roles,
      execute(ctx, input) {
        recent(input.fields);
        const centsPerMinute = Number(input.fields?.centsPerMinute),
          storageCentsPerGiB = Number(input.fields?.storageCentsPerGiB);
        priceEstimate(1, 1, { centsPerMinute, storageCentsPerGiB });
        if (
          input.fields?.fingerprint !==
          JSON.stringify({ centsPerMinute, storageCentsPerGiB })
        )
          fail('单价已变化，请重新预览', 'CONFLICT');
        const data = { ...structuredClone(ctx.data()) };
        delete data.collectionReceipts;
        const prices = (data.prices || []) as unknown as PricingVersion[];
        prices.push({
          uuid: crypto.randomUUID(),
          version: prices.length + 1,
          publishedAt: ctx.now,
          centsPerMinute,
          storageCentsPerGiB,
        });
        ctx.setData({ ...data, prices } as unknown as JsonObject);
        return undefined;
      },
    },
  },
  migrations: [
    {
      version: 1,
      up(s) {
        s.domainData ||= {};
        s.domainData.operations ||= { prices: [] };
      },
    },
  ],
  query(ctx, input) {
    const scope = {
      campus: ctx.context.campus,
      from: input.from || `${ctx.now.slice(0, 10)}T00:00:00+08:00`,
      to: input.to || `${ctx.now.slice(0, 10)}T23:59:59.999+08:00`,
    };
    return {
      metrics: metricSelector(ctx, scope),
      backlog: backlog(ctx, scope),
    };
  },
  metrics(ctx) {
    const scope = {
      campus: ctx.context.campus,
      from: '1970-01-01T00:00:00Z',
      to: '2100-01-01T00:00:00Z',
    };
    return metricSelector(ctx, scope).map((m) => ({
      uuid: `operation:${m.definition.id}`,
      scope: scope.campus,
      asOf: ctx.now,
      metric: m.definition.label,
      numerator: m.numerator,
      denominator: m.denominator,
      unit: m.definition.unit,
      sourceUuids: m.sourceUuids,
      definitionVersion: m.definition.version,
    }));
  },
};
export const pilotDomain: DomainModule = {
  namespace: 'pilot',
  resource: 'pilot',
  version: 1,
  migrations: [
    {
      version: 1,
      up(s) {
        for (const row of s.entities.filter((r) => r.kind === 'pilot')) {
          if (row.fields?.pilotState) continue;
          row.fields = {
            ...row.fields,
            pilotState: JSON.stringify({
              goals: [],
              evidence: [],
              reports: [],
              decisions: [],
              deviceUuids: [],
              taskUuids: [],
            }),
          };
        }
      },
    },
  ],
  commands: {
    create: {
      roles,
      kind: 'pilot',
      execute(ctx, input) {
        const campus = input.fields?.campus || ctx.context.campus;
        if (
          campus === '全部校区' ||
          (ctx.context.campus !== '全部校区' &&
            campus !== ctx.context.campus) ||
          !ctx.members().some((m) => m.campus === campus)
        )
          fail('请选择授权校区');
        validateScope({
          campus,
          from: input.startAt || '',
          to: input.fields?.endAt || '',
        });
        if (
          !input.title?.trim() ||
          !input.fields?.owner?.trim() ||
          !input.dueAt ||
          Date.parse(input.dueAt) < Date.parse(input.startAt || '') ||
          Date.parse(input.dueAt) >= Date.parse(input.fields?.endAt || '')
        )
          fail('名称、负责人、范围周期与周期内评审日期必填');
        const row = ctx.append('pilot', input.title, {
          campus,
          owner: input.fields?.owner,
          startAt: input.startAt,
          endAt: input.fields?.endAt,
          dueAt: input.dueAt,
          status: 'draft',
        });
        return write(ctx, row, pilotOf(row));
      },
    },
    schedule: {
      roles,
      kind: 'pilot',
      execute(ctx, input) {
        const row = target(ctx, input.uuid);
        validateScope({
          campus: row.campus,
          from: input.startAt || '',
          to: input.fields?.endAt || '',
        });
        if (
          !input.fields?.owner?.trim() ||
          !input.dueAt ||
          !Number.isFinite(Date.parse(input.dueAt)) ||
          Date.parse(input.dueAt) < Date.parse(input.startAt || '') ||
          Date.parse(input.dueAt) >= Date.parse(input.fields?.endAt || '')
        )
          fail('负责人及周期内评审日期必填');
        row.startAt = input.startAt;
        row.endAt = input.fields?.endAt;
        row.dueAt = input.dueAt;
        row.owner = input.fields.owner;
        ctx.touch(row);
        return row;
      },
    },
    goals: {
      roles,
      kind: 'pilot',
      execute(ctx, input) {
        const row = target(ctx, input.uuid);
        const p = pilotOf(row);
        const goals = json<PilotGoal[]>(input.fields?.goals);
        if (
          !Array.isArray(goals) ||
          !goals.length ||
          goals.length > 30 ||
          goals.some(
            (g) =>
              !definitions.some((d) => d.id === g.metric) ||
              !g.scenario?.trim() ||
              !g.owner?.trim() ||
              !Number.isSafeInteger(g.target) ||
              g.target < 1,
          )
        )
          fail('目标需有有效指标、正整数目标、场景和负责人');
        const ids = new Set<string>();
        for (const goal of goals) {
          goal.uuid = goal.uuid || crypto.randomUUID();
          if (ids.has(goal.uuid)) fail('目标标识不能重复');
          ids.add(goal.uuid);
        }
        p.goals.push({
          version: p.goals.length + 1,
          at: ctx.now,
          items: goals,
        });
        row.status = 'inProgress';
        return write(ctx, row, p);
      },
    },
    link: {
      roles,
      kind: 'pilot',
      execute(ctx, input) {
        const row = target(ctx, input.uuid),
          p = pilotOf(row);
        const source = ctx.get(input.fields?.sourceUuid);
        if (
          !source ||
          source.campus !== row.campus ||
          !['device', 'task'].includes(source.kind)
        )
          fail('仅可关联同校区授权设备或任务');
        const ids = source.kind === 'device' ? p.deviceUuids : p.taskUuids;
        if (!ids.includes(source.uuid)) ids.push(source.uuid);
        return write(ctx, row, p);
      },
    },
    evidence: {
      roles,
      kind: 'pilot',
      execute(ctx, input) {
        const row = target(ctx, input.uuid),
          p = pilotOf(row);
        const goal = p.goals
          .at(-1)
          ?.items.find((g) => g.uuid === input.fields?.goalUuid);
        const source = ctx.get(input.fields?.sourceUuid);
        if (
          !goal ||
          !source ||
          source.kind !== kinds[goal.metric] ||
          source.campus !== row.campus ||
          !input.note?.trim()
        )
          fail('验收材料需匹配目标、校区、周期并填写人工结论');
        const metric = metricSelector(ctx, pilotScope(row)).find(
          (m) => m.definition.id === goal.metric,
        );
        if (!metric?.numeratorUuids.includes(source.uuid))
          fail('来源尚未达到该指标的有效状态');
        p.evidence = p.evidence.filter(
          (e) => !(e.goalUuid === goal.uuid && e.sourceUuid === source.uuid),
        );
        p.evidence.push({
          uuid: crypto.randomUUID(),
          goalUuid: goal.uuid,
          sourceUuid: source.uuid,
          sourceRevision: source.revision,
          conclusion: input.note,
          verifiedBy: ctx.identity.memberUuid,
          verifiedAt: ctx.now,
        });
        return write(ctx, row, p);
      },
    },
    report: {
      roles,
      kind: 'pilot',
      execute(ctx, input) {
        const row = target(ctx, input.uuid),
          p = pilotOf(row);
        recent(input.fields);
        const draft = reportDraft(
          ctx,
          row,
          input.note || '模拟评审材料，不代表真实业务收益',
        );
        if (input.fields?.fingerprint !== JSON.stringify(draft))
          fail('报告来源已变化，请重新预览', 'CONFLICT');
        p.reports.push({
          ...draft,
          uuid: crypto.randomUUID(),
          version: p.reports.length + 1,
        });
        row.status = 'pendingReview';
        return write(ctx, row, p);
      },
    },
    decide: {
      roles,
      kind: 'pilot',
      execute(ctx, input) {
        const row = target(ctx, input.uuid),
          p = pilotOf(row);
        const report = reportFor(ctx, row, input.fields?.reportUuid || '');
        if (
          !report ||
          p.reports.at(-1)?.uuid !== report.uuid ||
          !input.note?.trim()
        )
          fail('请选择最新授权报告并填写评审理由');
        const draft = reportDraft(ctx, row, report.note);
        if (
          report.goalVersion !== draft.goalVersion ||
          JSON.stringify(report.scope) !== JSON.stringify(draft.scope) ||
          JSON.stringify(
            report.metrics.map((m) => [
              m.definition.id,
              m.numerator,
              m.denominator,
              m.missing,
              m.sourceUuids,
            ]),
          ) !==
            JSON.stringify(
              draft.metrics.map((m) => [
                m.definition.id,
                m.numerator,
                m.denominator,
                m.missing,
                m.sourceUuids,
              ]),
            ) ||
          JSON.stringify(report.sourceVersions) !==
            JSON.stringify(draft.sourceVersions)
        )
          fail('来源或目标已变化，请生成新报告', 'CONFLICT');
        const decision = input.fields?.decision;
        if (!['advance', 'supplement', 'pause'].includes(decision || ''))
          fail('请选择人工决策');
        if (
          decision === 'advance' &&
          (report.missing.length || draft.missing.length)
        )
          fail('目标未达或证据不足，不能推进');
        if (p.decisions.some((d) => d.reportUuid === report.uuid))
          fail('本报告已评审，请生成新版本');
        p.decisions.push({
          at: ctx.now,
          actor: ctx.identity.memberUuid,
          reportUuid: report.uuid,
          decision: decision || '',
          note: input.note,
        });
        row.status =
          decision === 'advance'
            ? 'completed'
            : decision === 'pause'
              ? 'paused'
              : 'inProgress';
        return write(ctx, row, p);
      },
    },
  },
};
